import { css, html, nothing } from 'lit';
import { forecastTimelineEndpoints } from '../generated/endpoint-defaults.js';
import { aspectSymbol } from '../tokens/index.js';
import type {
	FindSignificantDatesResponse,
	ForecastTransitsResponse,
	GenerateTimelineResponse,
} from '../types/index.js';
import { RoxyDataElement } from '../utils/base-element.js';
import { baseStyles } from '../utils/base-styles.js';
import { customElement } from '../utils/define.js';
import {
	ASPECT_CLASS,
	formatAspectName,
	formatDate,
	formatDateRange,
	formatNumber,
	normalizeAspect,
} from '../utils/format.js';
import { humanize } from '../utils/string.js';

/** Timeline, significant-dates, and forecast-transits all return the same `{ events, startDate, endDate, birthData, count }` shape. */
type ForecastTimelineData =
	| GenerateTimelineResponse
	| FindSignificantDatesResponse
	| ForecastTransitsResponse;

type ForecastEvent = GenerateTimelineResponse['events'][number];
type ForecastDomain = ForecastEvent['domain'];

/**
 * Display label per forecast domain. Keyed by the spec `domain` union so a new
 * domain in the spec surfaces as a typecheck failure here rather than an
 * unlabeled swatch.
 */
const DOMAIN_LABEL: Record<ForecastDomain, string> = {
	western: 'Western',
	vedic: 'Vedic',
	biorhythm: 'Biorhythm',
};

const DOMAIN_ORDER: readonly ForecastDomain[] = [
	'western',
	'vedic',
	'biorhythm',
];

/**
 * Cross-domain forecast timeline. Pass `data` from /forecast/timeline. Events
 * are grouped by calendar date down a vertical axis, each colored by its domain
 * and weighted by significance (0-100), showing the event type, body, target,
 * aspect, and description. The visual language matches the dasha timeline: a
 * left rail of dates, a significance bar per event, and a colored marker for
 * the domain.
 *
 * Theming flows through `--roxy-*` custom properties on `:host`.
 *
 * @remarks
 * `hide-readings` drops only `description`. Every event keeps its date, its domain marker, its significance bar and the headline built from the structured `body`, `aspect`, `target` and `orb`, so nothing factual is lost: the sentence restates that headline in prose, which is exactly the report a page supplying its own words does not want repeated under every row.
 */
@customElement('roxy-forecast-timeline')
export class RoxyForecastTimeline extends RoxyDataElement<ForecastTimelineData> {
	static endpoints = forecastTimelineEndpoints;

	static styles = [
		baseStyles,
		css`.wrap{background:var(--roxy-surface,#fff);color:var(--roxy-fg,#0a0a0a);border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);padding:var(--roxy-space-lg,1.5rem);box-shadow:var(--roxy-shadow-sm);gap:var(--roxy-space-md,1rem);grid-template-columns:minmax(0,1fr);display:grid}.head{justify-content:space-between;align-items:baseline;gap:var(--roxy-space-sm,.5rem);flex-wrap:wrap;display:flex}.title{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);margin:0}.range{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-sm,.875rem);font-variant-numeric:tabular-nums}.legend{gap:var(--roxy-space-md,1rem);font-size:var(--roxy-text-xs,.75rem);color:var(--roxy-muted,#71717a);flex-wrap:wrap;display:flex}.legend .swatch{vertical-align:middle;border-radius:50%;width:10px;height:10px;margin-right:4px;display:inline-block}.swatch-western{background:var(--roxy-accent,#f59e0b)}.swatch-vedic{background:var(--roxy-info,#2563eb)}.swatch-biorhythm{background:var(--roxy-success,#16a34a)}.day{gap:var(--roxy-space-md,1rem);padding-block:var(--roxy-space-sm,.5rem);border-top:1px solid var(--roxy-border,#e4e4e7);grid-template-columns:4.5rem 1fr;display:grid}.day:first-of-type{border-top:none}@container (width<=30rem){.day{grid-template-columns:minmax(0,1fr)}}.day-date{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);font-variant-numeric:tabular-nums;padding-top:2px}.events{gap:var(--roxy-space-sm,.5rem);display:grid}.event{gap:var(--roxy-space-sm,.5rem);grid-template-columns:.6rem 1fr;align-items:start;display:grid}.dot{border-radius:50%;width:.6rem;height:.6rem;margin-top:4px}.dot-western{background:var(--roxy-accent,#f59e0b)}.dot-vedic{background:var(--roxy-info,#2563eb)}.dot-biorhythm{background:var(--roxy-success,#16a34a)}.event-body{min-width:0}.event-line{font-size:var(--roxy-text-sm,.875rem);color:var(--roxy-fg,#0a0a0a)}.event-line .aspect-trine,.event-line .aspect-sextile{color:var(--_success-fg)}.event-line .aspect-square,.event-line .aspect-opposition{color:var(--_danger-fg)}.event-line .aspect-conjunction{color:var(--roxy-accent-ink,#b45309)}.event-line .kind{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);text-transform:uppercase;letter-spacing:.04em;margin-left:.35em}.event-desc{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);margin:2px 0 0}.sig{align-items:center;gap:.4rem;margin-top:4px;display:flex}.sig-track{background:var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-full,9999px);flex:1;max-width:8rem;height:4px;overflow:hidden}.sig-fill{border-radius:var(--roxy-radius-full,9999px);height:100%;display:block}.sig-fill-western{background:var(--roxy-accent,#f59e0b)}.sig-fill-vedic{background:var(--roxy-info,#2563eb)}.sig-fill-biorhythm{background:var(--roxy-success,#16a34a)}.sig-val{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-xs,.75rem);font-variant-numeric:tabular-nums}`,
	];

	protected renderData(d: ForecastTimelineData) {
		const events = d.events ?? [];
		const grouped = this.groupByDate(events);
		const present = DOMAIN_ORDER.filter((dom) =>
			events.some((e) => e.domain === dom),
		);

		return html`<div class="wrap" part="card" aria-label="${this.t('Forecast timeline')}"><header class="head" part="header"><h2 class="title">${this.t('Forecast timeline')}</h2>${
					d.startDate && d.endDate
						? html`<div class="range">${formatDateRange(this.effectiveLang(), d.startDate, d.endDate)} · ${this.t('{{count}} events', { count: d.count ?? events.length })}</div>`
						: nothing
				}</header>${
				present.length
					? html`<div class="legend" part="legend">${present.map(
							(dom) =>
								html`<span><span class="swatch swatch-${dom}"></span>${DOMAIN_LABEL[dom]}</span>`,
						)}</div>`
					: nothing
			} ${
				grouped.length
					? html`<div class="days" part="section events" role="list">${grouped.map(([date, dayEvents]) => this.renderDay(date, dayEvents))}</div>`
					: html`<p class="roxy-empty" role="status">${this.t('No events in this window')}</p>`
			}</div>`;
	}

	/** Group events by their calendar date, preserving response order. */
	private groupByDate(
		events: ForecastEvent[],
	): Array<[string, ForecastEvent[]]> {
		const map = new Map<string, ForecastEvent[]>();
		for (const e of events) {
			const key = e.date ?? '';
			const list = map.get(key) ?? [];
			list.push(e);
			map.set(key, list);
		}
		return [...map.entries()];
	}

	private renderDay(date: string, events: ForecastEvent[]) {
		return html`<div class="day" role="listitem"><div class="day-date">${formatDate(this.effectiveLang(), date)}</div><div class="events">${events.map((e) => this.renderEvent(e))}</div></div>`;
	}

	private renderEvent(e: ForecastEvent) {
		const sig = typeof e.significance === 'number' ? e.significance : 0;
		const width = Math.max(0, Math.min(100, sig));
		return html`<div class="event"><span class="dot dot-${e.domain}" aria-hidden="true"></span><div class="event-body"><div class="event-line">${this.renderHeadline(e)}</div>${
					// The headline above already carries the bodies, the aspect and the orb
					// from the structured fields, so this sentence is the written read of a
					// row that stays complete without it.
					e.description && !this.hideReadings
						? html`<p class="event-desc">${e.description}</p>`
						: nothing
				}<div class="sig" title="${this.t('significance {{value}} of 100', { value: width })}"><span class="sig-track"><span class="sig-fill sig-fill-${e.domain}" style="width:${width}%"></span> </span><span class="sig-val">${width}</span></div></div></div>`;
	}

	/**
	 * Compact event headline built from the spec fields. Transit aspects read
	 * "body aspect target"; ingresses, stations, eclipses, dasha changes, and
	 * critical days fall back to "body" plus the qualifier the spec carries for
	 * that type (station direction, eclipse kind). The aspect symbol is colored
	 * by nature, matching the chart aspect encoding.
	 */
	private renderHeadline(e: ForecastEvent) {
		// `humanize`, never `capitalize`: a body is a proper name the response already
		// cases (`Black Moon Lilith`, `South Node`), and a cycle is one lowercase word.
		const body = e.body ? humanize(e.body) : '';
		const target = e.target ? humanize(e.target) : '';
		const aspect = normalizeAspect({ type: e.aspect });
		const aspectClass = ASPECT_CLASS[aspect] ?? '';
		const aspectSym = aspect
			? (aspectSymbol(e.aspect) ?? formatAspectName({ type: e.aspect }))
			: '';
		const orb =
			typeof e.orb === 'number'
				? formatNumber(this.effectiveLang(), e.orb, 1)
				: '';
		const qualifier = this.typeQualifier(e);

		if (aspect && target) {
			return html`<strong>${body}</strong> <span class="${aspectClass}">${aspectSym}</span> <strong>${target}</strong>${orb ? html`<span class="kind">${this.t('orb {{value}}°', { value: orb })}</span>` : nothing}`;
		}
		return html`<strong>${body || humanize(e.type ?? '')}</strong>${
			qualifier ? html`<span class="kind">${qualifier}</span>` : nothing
		}`;
	}

	/** Type-specific qualifier text from the optional spec fields. */
	private typeQualifier(e: ForecastEvent): string {
		if (e.type === 'sign-ingress' && e.target)
			return `enters ${humanize(e.target)}`;
		if (e.type === 'retrograde-station' && e.station) return e.station;
		if (e.type === 'eclipse')
			return [e.kind, 'eclipse'].filter(Boolean).join(' ');
		if (e.type === 'critical-day') return 'critical day';
		if (e.type === 'dasha-change' && e.target)
			return `dasha ${humanize(e.target)}`;
		return humanize(e.type ?? '');
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'roxy-forecast-timeline': RoxyForecastTimeline;
	}
}
